import { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiCopy, FiExternalLink, FiMessageCircle, FiMail, FiAlertCircle } from 'react-icons/fi';
import './PowerPaymentForm.css';

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://paymentsapi.steponexp.com/api',
  timeout: 30000,
});

const CONTACT_PHONE = '+91 86570 22012';
const REPORTING_EMAIL = 'ayush@steponexp.com';

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[6-9][0-9]{9}$/; // matches backend: must start 6-9
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const RAZORPAY_CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
let razorpayScriptPromise = null;
function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${RAZORPAY_CHECKOUT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const script = document.createElement('script');
    script.src = RAZORPAY_CHECKOUT_SRC;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

/* ---------- Booth category / deposit pricing rules ----------
   Base deposit ₹50,000 covers any booth up to 12 sqm (Silver).
   Beyond 12 sqm: +₹5,000 for every additional 6 sqm (rounded UP
   to the next slab for custom sizes that don't land on an exact
   multiple of 6).
------------------------------------------------------------- */
const BASE_DEPOSIT = 1;
const BASE_AREA = 12;       // sqm covered by the base deposit
const INCREMENT_AREA = 6;   // sqm per additional slab
const INCREMENT_AMOUNT = 5000;

const BOOTH_TIERS = [
  { key: 'silver',     label: 'Silver (4×3)',      length: 4,  width: 3, deposit: 1 },
  { key: 'gold',       label: 'Gold (6×3)',        length: 6,  width: 3, deposit: 55000 },
  { key: 'diamond',    label: 'Diamond (6×6)',     length: 6,  width: 6, deposit: 70000 },
  { key: 'platinum',   label: 'Platinum (8×6)',    length: 8,  width: 6, deposit: 80000 },
  { key: 'poweredby',  label: 'Powered by (10×6)', length: 10, width: 6, deposit: 90000 },
  { key: 'presenting', label: 'Presenting (12×8)', length: 12, width: 8, deposit: 120000 },
  { key: 'custom',     label: 'Other / Custom size', length: null, width: null, deposit: null },
];

// Rounds UP to the next 6-sqm slab beyond the base 12 sqm — so a
// booth of, say, 13–18 sqm pays the same +5k as an exact 18 sqm booth.
function computeDepositFromArea(area) {
  const a = Number(area) || 0;
  if (a <= BASE_AREA) return BASE_DEPOSIT;
  const extraUnits = Math.ceil((a - BASE_AREA) / INCREMENT_AREA);
  return BASE_DEPOSIT + extraUnits * INCREMENT_AMOUNT;
}

// ── Per-field validators — each returns an error string or null ────────────
const fieldValidators = {
  boothNo: (v) => (!v.trim() ? 'Booth number is required' : null),
  boothName: (v) => (!v.trim() ? 'Booth name is required' : null),
  companyName: (v) => (!v.trim() ? 'Company name is required' : null),
  companyAddress: (v) => (!v.trim() ? 'Company address is required' : null),
  companyGst: (v) => (!GST_REGEX.test(v.trim().toUpperCase()) ? 'Enter a valid 15-character GST number' : null),
  companyPocPhone: (v) => (!PHONE_REGEX.test(v.trim()) ? 'Enter a valid 10-digit phone number' : null),
  companyPocEmail: (v) => (!EMAIL_REGEX.test(v.trim()) ? 'Enter a valid email address' : null),

  vendorName: (v) => (!v.trim() ? 'Vendor name is required' : null),
  vendorGst: (v) => (v.trim() && !GST_REGEX.test(v.trim().toUpperCase()) ? 'Enter a valid GST number' : null),
  vendorPocPhone: (v) => (v.trim() && !PHONE_REGEX.test(v.trim()) ? 'Enter a valid 10-digit phone number' : null),
  vendorPocEmail: (v) => (v.trim() && !EMAIL_REGEX.test(v.trim()) ? 'Enter a valid email address' : null),

  bankName: (v) => (!v.trim() ? 'Bank name is required' : null),
  bankIfsc: (v) => (!IFSC_REGEX.test(v.trim().toUpperCase()) ? 'Enter a valid IFSC code (e.g. HDFC0001234)' : null),
  bankAccountName: (v) => (!v.trim() ? 'Account holder name is required' : null),
  bankAccountNumber: (v) => (!/^[0-9]{9,18}$/.test(v.trim()) ? 'Enter a valid account number' : null),
  bankAddress: (v) => (!v.trim() ? 'Bank / branch address is required' : null),

  boothLength: (v) => {
  const n = Number(v);
  if (!v || n <= 0) return 'Enter booth length (m)';
  if (n < 4) return 'Minimum length is 4 m';
  if (n > 12) return 'Maximum length is 12 m';
  return null;
},
boothWidth: (v) => {
  const n = Number(v);
  if (!v || n <= 0) return 'Enter booth width (m)';
  if (n < 3) return 'Minimum width is 3 m';
  if (n > 8) return 'Maximum width is 8 m';
  return null;
},
};

// Small helper — renders an error message below a field when present
function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, color: '#e74c3c', marginTop: 4 }}>
      <FiAlertCircle size={12} /> {msg}
    </span>
  );
}

export default function SecurityDepositForm() {
  const [step, setStep] = useState(1);

  const [eventName] = useState('People Matters TechHR India 2026');
  const [boothNo, setBoothNo] = useState('');
  const [boothName, setBoothName] = useState('');

  const [company, setCompany] = useState({
    name: '', address: '', gst: '', pocPhone: '', pocEmail: '',
  });

  const [hasVendor, setHasVendor] = useState(false);
  const [vendor, setVendor] = useState({
    name: '', address: '', gst: '', pocPhone: '', pocEmail: '',
  });

  const [bank, setBank] = useState({
    bankName: '', ifsc: '', accountName: '', accountNumber: '', address: '',
  });

  // ── Booth category / deposit state ──────────────────────────────────────
  const [boothTierKey, setBoothTierKey] = useState('silver');
  const [customLength, setCustomLength] = useState('');
  const [customWidth, setCustomWidth] = useState('');
  const [depositAmount, setDepositAmount] = useState(BOOTH_TIERS[0].deposit);

  const selectedTier = BOOTH_TIERS.find((t) => t.key === boothTierKey) || BOOTH_TIERS[0];
  const customArea = (Number(customLength) || 0) * (Number(customWidth) || 0);
  const computedDeposit = boothTierKey === 'custom'
    ? computeDepositFromArea(customArea)
    : selectedTier.deposit;

  // Keep depositAmount in sync whenever the tier or custom dimensions change
  useEffect(() => {
    setDepositAmount(computedDeposit);
  }, [computedDeposit]);

  const [errors, setErrors] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [paidResult, setPaidResult] = useState(null); // { txnId, paymentId }
  const [copyDone, setCopyDone] = useState(false);

  // Validate a single field and update the errors object accordingly.
  // If the field is now valid, its error key is removed — this is what
  // makes the error message disappear automatically as the person types.
  const runFieldValidation = (key, value) => {
    const validator = fieldValidators[key];
    if (!validator) return;
    const msg = validator(value);
    setErrors((prev) => {
      const next = { ...prev };
      if (msg) next[key] = msg;
      else delete next[key];
      return next;
    });
  };

  const updateBoothNo = (value) => {
    // Allow only letters, numbers and hyphen
    value = value.replace(/[^a-zA-Z0-9-]/g, '');

    // First character must be a letter
    value = value.replace(/^[^a-zA-Z]+/, '');

    // Capitalize the first letter
    if (value.length > 0) {
      value = value.charAt(0).toUpperCase() + value.slice(1);
    }

    setBoothNo(value);
    runFieldValidation('boothNo', value);
  };

  const updateBoothName = (value) => {
    setBoothName(value);
    runFieldValidation('boothName', value);
  };

  const updateCompany = (field, value) => {
    setCompany((c) => ({ ...c, [field]: value }));
    const keyMap = { name: 'companyName', address: 'companyAddress', gst: 'companyGst', pocPhone: 'companyPocPhone', pocEmail: 'companyPocEmail' };
    runFieldValidation(keyMap[field], value);
  };

  const updateVendor = (field, value) => {
    setVendor((v) => ({ ...v, [field]: value }));
    const keyMap = { name: 'vendorName', address: 'vendorAddress', gst: 'vendorGst', pocPhone: 'vendorPocPhone', pocEmail: 'vendorPocEmail' };
    runFieldValidation(keyMap[field], value);
  };

  const updateBank = (field, value) => {
    setBank((b) => ({ ...b, [field]: value }));
    const keyMap = { bankName: 'bankName', ifsc: 'bankIfsc', accountName: 'bankAccountName', accountNumber: 'bankAccountNumber', address: 'bankAddress' };
    runFieldValidation(keyMap[field], value);
  };

  const updateCustomLength = (value) => {
  // Strip decimals (digits only)
  value = value.replace(/[^0-9]/g, '');
  // Clamp into the allowed range once there's a number to clamp
  if (value !== '') {
    let n = Number(value);
    if (n > 12) n = 12;
    value = String(n);
  }
  setCustomLength(value);
  runFieldValidation('boothLength', value);
};

const updateCustomWidth = (value) => {
  value = value.replace(/[^0-9]/g, '');
  if (value !== '') {
    let n = Number(value);
    if (n > 8) n = 8;
    value = String(n);
  }
  setCustomWidth(value);
  runFieldValidation('boothWidth', value);
};

  const fmtAmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Full validation pass — run on Continue click. Populates `errors` for
  // every field at once so all problems show up together.
  const validateStep1 = () => {
    const next = {};

    const check = (key, value) => {
      const msg = fieldValidators[key]?.(value);
      if (msg) next[key] = msg;
    };

    check('boothNo', boothNo);
    check('boothName', boothName);
    check('companyName', company.name);
    check('companyAddress', company.address);
    check('companyGst', company.gst);
    check('companyPocPhone', company.pocPhone);
    check('companyPocEmail', company.pocEmail);

    if (hasVendor) {
      check('vendorName', vendor.name);
      check('vendorGst', vendor.gst);
      check('vendorPocPhone', vendor.pocPhone);
      check('vendorPocEmail', vendor.pocEmail);
    }

    check('bankName', bank.bankName);
    check('bankIfsc', bank.ifsc);
    check('bankAccountName', bank.accountName);
    check('bankAccountNumber', bank.accountNumber);
    check('bankAddress', bank.address);

    if (boothTierKey === 'custom') {
      check('boothLength', customLength);
      check('boothWidth', customWidth);
    }

    setErrors(next);
    return next;
  };

  const goToStep2 = () => {
    const next = validateStep1();
    if (Object.keys(next).length > 0) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    setStep(2);
  };

  const handleCheckout = async () => {
  setSubmitting(true);
  try {
    const boothSize = boothTierKey === 'custom'
      ? { length: Number(customLength), width: Number(customWidth), area: customArea }
      : { length: selectedTier.length, width: selectedTier.width, area: selectedTier.length * selectedTier.width };

    const payload = {
      eventName, boothNo, boothName,
      boothCategory: selectedTier.label,
      boothSize,
      amount: Number(depositAmount),
      description: `Security Deposit - ${eventName} | Booth ${boothNo} (${boothName}) | ${selectedTier.label} | ${company.name}`,
      companyDetails: { name: company.name, address: company.address, gst: company.gst, pocPhone: company.pocPhone, pocEmail: company.pocEmail },
      vendorDetails: hasVendor ? { name: vendor.name, address: vendor.address, gst: vendor.gst, pocPhone: vendor.pocPhone, pocEmail: vendor.pocEmail } : null,
      bankDetails: { bankName: bank.bankName, ifsc: bank.ifsc.toUpperCase(), accountName: bank.accountName, accountNumber: bank.accountNumber, address: bank.address },
      name: company.name, phone: company.pocPhone, email: company.pocEmail,
    };

    const { data } = await publicApi.post('/razorpay/public/security-deposit-order', payload);
    if (!data.success || !data.razorpayOrderId) throw new Error(data.message || 'Order creation failed');

    const scriptOk = await loadRazorpayScript();
    if (!scriptOk) {
      toast.error('Unable to load the payment gateway. Check your connection and try again.');
      setSubmitting(false);
      return;
    }

    const options = {
      key: data.razorpayKeyId,
      amount: data.amount,
      currency: data.currency,
      name: 'StepOnExp',
      description: data.description,
      order_id: data.razorpayOrderId,
      prefill: { name: data.customerName, email: data.customerEmail, contact: data.customerPhone },
      theme: { color: '#2563eb' },
      handler: async (response) => {
        try {
          const verifyRes = await publicApi.post('/razorpay/public/verify', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            merchantTransactionId: data.merchantTransactionId,
          });
          if (verifyRes.data.success) {
            setPaidResult({ txnId: data.merchantTransactionId, paymentId: response.razorpay_payment_id });
            toast.success('Payment successful!');
            setStep(3);
          } else {
            toast.error('Payment verification failed. Please contact support.');
          }
        } catch {
          toast.error('Payment verification failed. Please contact support.');
        } finally {
          setSubmitting(false);
        }
      },
      modal: { ondismiss: () => setSubmitting(false) },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', () => { toast.error('Payment failed. Please try again.'); setSubmitting(false); });
    rzp.open();
  } catch (e) {
    toast.error(e.response?.data?.message || e.message || 'Something went wrong');
    setSubmitting(false);
  }
};

  const copyLink = () => {
    if (!paymentLink) return;
    navigator.clipboard.writeText(paymentLink.link);
    setCopyDone(true);
    toast.success('Link copied!');
    setTimeout(() => setCopyDone(false), 2000);
  };

  const shareOnWhatsApp = () => {
    if (!paymentLink) return;
    const amt = fmtAmt(depositAmount);
    const msg =
      `Hi ${company.name || ''},\n\nPlease complete your security deposit payment of ₹${amt} ` +
      `for *${eventName}* (Booth ${boothNo} - ${boothName}) using this link:\n${paymentLink.link}\n\n` +
      `Transaction ID: ${paymentLink.txnId}\n\n- StepOnExp`;
    window.open(`https://wa.me/${company.pocPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const shareViaEmail = () => {
    if (!paymentLink) return;
    if (!company.pocEmail) return toast.error('POC email is required');
    const amt = fmtAmt(depositAmount);
    const subject = `Security Deposit Request - ${eventName} - Booth ${boothNo}`;
    const body =
      `Hi ${company.name || ''},\n\nPlease complete your security deposit payment of ₹${amt} ` +
      `for ${eventName} (Booth ${boothNo} - ${boothName}) using this link:\n${paymentLink.link}\n\nTransaction ID: ${paymentLink.txnId}`;
    window.open(
      `mailto:${company.pocEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      '_blank'
    );
    toast.success('Email client opened');
  };

  const resetForm = () => {
    setStep(1);
    setBoothNo('');
    setBoothName('');
    setCompany({ name: '', address: '', gst: '', pocPhone: '', pocEmail: '' });
    setHasVendor(false);
    setVendor({ name: '', address: '', gst: '', pocPhone: '', pocEmail: '' });
    setBank({ bankName: '', ifsc: '', accountName: '', accountNumber: '', address: '' });
    setBoothTierKey('silver');
    setCustomLength('');
    setCustomWidth('');
    setErrors({});
    setPaymentLink(null);
    setCopyDone(false);
  };

  // Adds a red border to an input when its field key has an active error
  const errStyle = (key) => errors[key] ? { borderColor: '#e74c3c', background: '#fff6f6' } : undefined;

  return (
    <div className="ppf-page">
      <div className="ppf-shell">
        <header className="ppf-header">
          <div className="ppf-mark">
            <img src="/logo.svg" alt="SteponePay" style={{ width: '100%', height: '100%', padding: 8 }} />
          </div>
          <div>
            <h1>Security Deposit</h1>
            <p>Refundable deposit for your booth / stall</p>
          </div>
          <div className="ppf-steps">
            <span className={step === 1 ? 'active' : 'done'}>1. Details</span>
            <span className="ppf-step-divider" />
            <span className={step === 2 ? 'active' : step > 2 ? 'done' : ''}>2. Review & checkout</span>
            <span className="ppf-step-divider" />
            <span className={step === 3 ? 'active' : ''}>3. Payment link</span>
          </div>
        </header>

        {step === 1 && (
          <section className="ppf-card">
            <h2>Booth & company details</h2>

            <label className="ppf-field">
              <span>Booth No.</span>
              <input
                type="text"
                value={boothNo}
                onChange={(e) => updateBoothNo(e.target.value)}
                placeholder="e.g. A-14"
                style={errStyle('boothNo')}
              />
              <FieldError msg={errors.boothNo} />
            </label>
            <label className="ppf-field">
              <span>Booth Name</span>
              <input
                type="text"
                value={boothName}
                onChange={(e) => updateBoothName(e.target.value)}
                placeholder="e.g. StepOne Experiential Zone"
                style={errStyle('boothName')}
              />
              <FieldError msg={errors.boothName} />
            </label>

            <div className="ppf-divider">Company details</div>

            <div className="ppf-grid">
              <label className="ppf-field">
                <span>Company Name</span>
                <input
                  type="text"
                  value={company.name}
                  onChange={(e) => updateCompany('name', e.target.value)}
                  placeholder="e.g. StepOne"
                  style={errStyle('companyName')}
                />
                <FieldError msg={errors.companyName} />
              </label>

              <label className="ppf-field">
                <span>GST Number</span>
                <input
                  type="text"
                  value={company.gst}
                  onChange={(e) => updateCompany('gst', e.target.value.toUpperCase())}
                  placeholder="XXXXXXXXXXXXXXX"
                  maxLength={15}
                  style={errStyle('companyGst')}
                />
                <FieldError msg={errors.companyGst} />
              </label>

              <label className="ppf-field ppf-span-2">
                <span>Address</span>
                <input
                  type="text"
                  value={company.address}
                  onChange={(e) => updateCompany('address', e.target.value)}
                  placeholder="Registered address"
                  style={errStyle('companyAddress')}
                />
                <FieldError msg={errors.companyAddress} />
              </label>

              <label className="ppf-field">
                <span>POC Phone</span>
                <input
                  type="tel"
                  value={company.pocPhone}
                  onChange={(e) => updateCompany('pocPhone', e.target.value.replace(/\D/g, ''))}
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  style={errStyle('companyPocPhone')}
                />
                <FieldError msg={errors.companyPocPhone} />
              </label>

              <label className="ppf-field">
                <span>POC Email</span>
                <input
                  type="email"
                  value={company.pocEmail}
                  onChange={(e) => updateCompany('pocEmail', e.target.value)}
                  placeholder="poc@email.com"
                  style={errStyle('companyPocEmail')}
                />
                <FieldError msg={errors.companyPocEmail} />
              </label>
            </div>

            <div className="ppf-divider">
              Vendor details
              <label style={{ float: 'right', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={hasVendor}
                  onChange={(e) => {
                    setHasVendor(e.target.checked);
                    if (!e.target.checked) {
                      // Clear vendor errors when vendor section is hidden again
                      setErrors((prev) => {
                        const next = { ...prev };
                        ['vendorName', 'vendorGst', 'vendorPocPhone', 'vendorPocEmail'].forEach((k) => delete next[k]);
                        return next;
                      });
                    }
                  }}
                  style={{ marginRight: 6 }}
                />
                Add vendor (optional)
              </label>
            </div>

            {hasVendor && (
              <div className="ppf-grid">
                <label className="ppf-field">
                  <span>Vendor Name</span>
                  <input
                    type="text"
                    value={vendor.name}
                    onChange={(e) => updateVendor('name', e.target.value)}
                    placeholder="Vendor / contractor name"
                    style={errStyle('vendorName')}
                  />
                  <FieldError msg={errors.vendorName} />
                </label>

                <label className="ppf-field">
                  <span>Vendor GST Number</span>
                  <input
                    type="text"
                    value={vendor.gst}
                    onChange={(e) => updateVendor('gst', e.target.value.toUpperCase())}
                    placeholder="XXXXXXXXXXXXXXX"
                    maxLength={15}
                    style={errStyle('vendorGst')}
                  />
                  <FieldError msg={errors.vendorGst} />
                </label>

                <label className="ppf-field ppf-span-2">
                  <span>Vendor Address</span>
                  <input
                    type="text"
                    value={vendor.address}
                    onChange={(e) => updateVendor('address', e.target.value)}
                    placeholder="Vendor address"
                  />
                </label>

                <label className="ppf-field">
                  <span>Vendor POC Phone</span>
                  <input
                    type="tel"
                    value={vendor.pocPhone}
                    onChange={(e) => updateVendor('pocPhone', e.target.value.replace(/\D/g, ''))}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    style={errStyle('vendorPocPhone')}
                  />
                  <FieldError msg={errors.vendorPocPhone} />
                </label>

                <label className="ppf-field">
                  <span>Vendor POC Email</span>
                  <input
                    type="email"
                    value={vendor.pocEmail}
                    onChange={(e) => updateVendor('pocEmail', e.target.value)}
                    placeholder="vendor@email.com"
                    style={errStyle('vendorPocEmail')}
                  />
                  <FieldError msg={errors.vendorPocEmail} />
                </label>
              </div>
            )}

            <div className="ppf-divider">Bank details (for refund)</div>

            <div className="ppf-grid">
              <label className="ppf-field">
                <span>Bank Name</span>
                <input
                  type="text"
                  value={bank.bankName}
                  onChange={(e) => updateBank('bankName', e.target.value)}
                  placeholder="e.g. HDFC Bank"
                  style={errStyle('bankName')}
                />
                <FieldError msg={errors.bankName} />
              </label>

              <label className="ppf-field">
                <span>IFSC Code</span>
                <input
                  type="text"
                  value={bank.ifsc}
                  onChange={(e) => updateBank('ifsc', e.target.value.toUpperCase())}
                  placeholder="e.g. HDFC0001234"
                  maxLength={11}
                  style={errStyle('bankIfsc')}
                />
                <FieldError msg={errors.bankIfsc} />
              </label>

              <label className="ppf-field">
                <span>Account Holder Name</span>
                <input
                  type="text"
                  value={bank.accountName}
                  onChange={(e) => updateBank('accountName', e.target.value)}
                  placeholder="As per bank records"
                  style={errStyle('bankAccountName')}
                />
                <FieldError msg={errors.bankAccountName} />
              </label>

              <label className="ppf-field">
                <span>Account Number</span>
                <input
                  type="text"
                  value={bank.accountNumber}
                  onChange={(e) => updateBank('accountNumber', e.target.value.replace(/\D/g, ''))}
                  placeholder="Bank account number"
                  maxLength={18}
                  style={errStyle('bankAccountNumber')}
                />
                <FieldError msg={errors.bankAccountNumber} />
              </label>

              <label className="ppf-field ppf-span-2">
                <span>Bank / Branch Address</span>
                <input
                  type="text"
                  value={bank.address}
                  onChange={(e) => updateBank('address', e.target.value)}
                  placeholder="Branch address"
                  style={errStyle('bankAddress')}
                />
                <FieldError msg={errors.bankAddress} />
              </label>
            </div>

            <div className="ppf-divider">Booth category & deposit</div>

            <label className="ppf-field">
              <span>Booth Category</span>
              <select value={boothTierKey} onChange={(e) => setBoothTierKey(e.target.value)}>
                {BOOTH_TIERS.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </label>

            {boothTierKey === 'custom' ? (
              <div className="ppf-grid">
                <label className="ppf-field">
                  <span>Length (m)</span>
                  <input
                    type="number"
                    min="4"
                    max="12"
                    step="1"
                    onKeyDown={(e) => { if (['.', 'e', 'E', '-', '+'].includes(e.key)) e.preventDefault(); }}
                    value={customLength}
                    onChange={(e) => updateCustomLength(e.target.value)}
                    placeholder="e.g. 6"
                    style={errStyle('boothLength')}
                  />
                  <FieldError msg={errors.boothLength} />
                </label>
                <label className="ppf-field">
                  <span>Width (m)</span>
                  <input
                    type="number"
                    min="3"
                    max="8"
                    step="1"
                    onKeyDown={(e) => { if (['.', 'e', 'E', '-', '+'].includes(e.key)) e.preventDefault(); }}
                    value={customWidth}
                    onChange={(e) => updateCustomWidth(e.target.value)}
                    placeholder="e.g. 6"
                    style={errStyle('boothWidth')}
                  />
                  <FieldError msg={errors.boothWidth} />
                </label>
              </div>
            ) : (
              <p className="ppf-note-text">
                Booth size: {selectedTier.length} × {selectedTier.width} m ({selectedTier.length * selectedTier.width} sqm)
              </p>
            )}

            <div className="ppf-summary">
              <div className="ppf-summary-row ppf-summary-total">
                <span>Security deposit</span>
                <span>₹{fmtAmt(depositAmount)}</span>
              </div>
            </div>

            <p className="ppf-note-text">
              <strong>Refund Policy:</strong> The security deposit is fully refundable and will be credited to the bank account provided above within 30 - 45 days after the event concludes, subject to successful booth clearance and damage inspection.
            </p>

            <div className="ppf-contact-box">
              <div>
                <span className="ppf-contact-label">Support</span>
                <span>{CONTACT_PHONE}</span>
              </div>
              <div>
                <span className="ppf-contact-label">Reporting</span>
                <span>{REPORTING_EMAIL}</span>
              </div>
            </div>

            <button className="ppf-btn-primary" onClick={goToStep2}>
              Continue To Review
            </button>
          </section>
        )}

        {step === 2 && (
          <section className="ppf-card">
            <h2>Review & checkout</h2>

            <p className="ppf-note-text">
              This deposit is fully refundable and will be transferred back to the bank details
              provided, within 30 - 45 days after event completion, subject to booth clearance checks.
            </p>

            <div className="ppf-summary">
              <div className="ppf-summary-row"><span>Booth No.</span><span>{boothNo}</span></div>
              <div className="ppf-summary-row"><span>Booth Name</span><span>{boothName}</span></div>
              <div className="ppf-summary-row"><span>Booth Category</span><span>{selectedTier.label}</span></div>
              <div className="ppf-summary-row">
                <span>Booth Size</span>
                <span>
                  {boothTierKey === 'custom'
                    ? `${customLength || 0} × ${customWidth || 0} m (${customArea} sqm)`
                    : `${selectedTier.length} × ${selectedTier.width} m (${selectedTier.length * selectedTier.width} sqm)`}
                </span>
              </div>
              <div className="ppf-summary-row"><span>Company</span><span>{company.name}</span></div>
              <div className="ppf-summary-row"><span>Company GST</span><span>{company.gst}</span></div>
              {hasVendor && (
                <div className="ppf-summary-row"><span>Vendor</span><span>{vendor.name}</span></div>
              )}
              <div className="ppf-summary-row"><span>Refund Account</span><span>{bank.accountName} • {bank.bankName}</span></div>
              <div className="ppf-summary-row"><span>IFSC</span><span>{bank.ifsc}</span></div>
              <div className="ppf-summary-row ppf-summary-total">
                <span>Security deposit</span>
                <span>₹{fmtAmt(depositAmount)}</span>
              </div>
            </div>

            <div className="ppf-actions">
              <button className="ppf-btn-secondary" onClick={() => setStep(1)}>
                Back
              </button>
              <button className="ppf-btn-primary" onClick={handleCheckout} disabled={submitting}>
                {submitting ? 'Generating payment link…' : 'Checkout'}
              </button>
            </div>
          </section>
        )}

        {/* {step === 3 && paymentLink && (
          <section className="ppf-card">
            <div className="ppf-s3-summary">
              <div className="ppf-s3-col">
                <span className="ppf-s3-label">Booth</span>
                <span className="ppf-s3-value">{boothNo}</span>
                <span className="ppf-s3-sub">{boothName} • {company.name}</span>
                <span className="ppf-s3-sub">{selectedTier.label}</span>
              </div>
              <div className="ppf-s3-divider" />
              <div className="ppf-s3-col">
                <span className="ppf-s3-label">Refund account</span>
                <span className="ppf-s3-value">{bank.accountName}</span>
                <span className="ppf-s3-sub">{bank.bankName} • {bank.ifsc}</span>
              </div>
              <div className="ppf-s3-divider" />
              <div className="ppf-s3-col ppf-s3-col--totals">
                <div className="ppf-s3-total-row ppf-s3-total-row--grand">
                  <span>Deposit</span><span>₹{fmtAmt(depositAmount)}</span>
                </div>
              </div>
            </div>

            <div className="ppf-result-banner">
              <div className="ppf-result-icon">✅</div>
              <div>
                <div className="ppf-result-banner-title">Payment link generated!</div>
                <div className="ppf-result-banner-sub">Share the link below - deposit can be paid via Card, UPI, Netbanking or Wallet.</div>
              </div>
            </div>

            <div className="ppf-result-amount-box">
              <span className="ppf-result-amount-label">Security deposit amount</span>
              <span className="ppf-result-amount-value">₹{fmtAmt(depositAmount)}</span>
            </div>

            <div className="ppf-result-link-box">
              <div className="ppf-result-link-label">🔗 Razorpay Payment Link</div>
              <a href={paymentLink.link} target="_blank" rel="noreferrer" className="ppf-result-link-url">
                {paymentLink.link}
              </a>
            </div>

            <div className="ppf-result-btn-grid">
              <a className="ppf-result-action ppf-result-action--primary" href={paymentLink.link} target="_blank" rel="noreferrer">
                <FiExternalLink /> Pay Now
              </a>
              <button className="ppf-result-action ppf-result-action--secondary" onClick={copyLink}>
                <FiCopy /> {copyDone ? '✅ Copied!' : 'Copy Link'}
              </button>
              <button className="ppf-result-action ppf-result-action--secondary" onClick={shareOnWhatsApp}>
                <FiMessageCircle /> WhatsApp
              </button>
              <button className="ppf-result-action ppf-result-action--secondary" onClick={shareViaEmail}>
                <FiMail /> Email
              </button>
            </div>

            <div className="ppf-result-txn-row">
              <span>Transaction ID:</span>
              <span className="ppf-mono">{paymentLink.txnId}</span>
              <button className="ppf-result-copy-btn" onClick={() => { navigator.clipboard.writeText(paymentLink.txnId); toast.success('Txn ID copied!'); }}>
                Copy
              </button>
            </div>

            <p className="ppf-result-note">
              This link will remain active. You can safely close this tab - the deposit request has been saved.
            </p>

            <button className="ppf-link-reset" onClick={resetForm}>
              ← Start a new deposit request
            </button>
          </section>
        )} */}

        {step === 3 && paidResult && (
  <section className="ppf-card">
    <div className="ppf-s3-summary">
      <div className="ppf-s3-col">
        <span className="ppf-s3-label">Booth</span>
        <span className="ppf-s3-value">{boothNo}</span>
        <span className="ppf-s3-sub">{boothName} • {company.name}</span>
        <span className="ppf-s3-sub">{selectedTier.label}</span>
      </div>
      <div className="ppf-s3-divider" />
      <div className="ppf-s3-col">
        <span className="ppf-s3-label">Refund account</span>
        <span className="ppf-s3-value">{bank.accountName}</span>
        <span className="ppf-s3-sub">{bank.bankName} • {bank.ifsc}</span>
      </div>
      <div className="ppf-s3-divider" />
      <div className="ppf-s3-col ppf-s3-col--totals">
        <div className="ppf-s3-total-row ppf-s3-total-row--grand"><span>Deposit</span><span>₹{fmtAmt(depositAmount)}</span></div>
      </div>
    </div>

    <div className="ppf-result-banner">
      {/* <div className="ppf-result-icon">✅</div> */}
      <div>
        <div className="ppf-result-banner-title">Payment successful!</div>
        <div className="ppf-result-banner-sub">Your security deposit has been received.</div>
      </div>
    </div>

    <div className="ppf-result-amount-box">
      <span className="ppf-result-amount-label">Security deposit paid</span>
      <span className="ppf-result-amount-value">₹{fmtAmt(depositAmount)}</span>
    </div>

    <div className="ppf-result-txn-row">
      <span>Transaction ID:</span>
      <span className="ppf-mono">{paidResult.txnId}</span>
      <button className="ppf-result-copy-btn" onClick={() => { navigator.clipboard.writeText(paidResult.txnId); toast.success('Txn ID copied!'); }}>Copy</button>
    </div>
    <div className="ppf-result-txn-row">
      <span>Payment ID:</span>
      <span className="ppf-mono">{paidResult.paymentId}</span>
      <button className="ppf-result-copy-btn" onClick={() => { navigator.clipboard.writeText(paidResult.paymentId); toast.success('Payment ID copied!'); }}>Copy</button>
    </div>

    <p className="ppf-result-note">
      A confirmation email has been sent to {company.pocEmail}. You can safely close this tab - the deposit has been saved.
    </p>

    <button className="ppf-link-reset" onClick={resetForm}>← Start a new deposit request</button>
  </section>
)}
      </div>
    </div>
  );
}